/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import { breakpoints, Spacings, Fonts } from '@styles/index';
import Toggle from '@icon/notes/Toggle';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Popper } from '@material-ui/core';
import FontsIcon from '@icon/Fonts';
import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { evtNameCategoryMap } from '@constants/index';
import { SYLLABUS_CLICKED_KEYS } from '@constants/segment';
import pushRoute from '@utils/pushRoute';
import ToggleShowNav from '@icon/notes/ToggleShowNav';
import { EPUB_NOTES, NOTES_TEXT_SETTINGS_HOVERED } from '../utils';
import SearchBox from './SearchBox';
import ThemePopover, { IThemePopoverFn } from './ThemePopover';
import { EpubContext } from '../Context/EpubContext';

interface HeaderProps {
  showNavigation: boolean;
  onNavigationClick: () => void;
  showPDFViewer: boolean;
}

interface IImageWrapper {
  $open?: boolean;
}

const LogoStyled = styled.img`
  user-drag: none;
  user-select: none;
  width: auto;
  grid-column: 2/5;
  height: ${Spacings.SPACING_6B};
`;

const LogoWrapper = styled.div`
  cursor: pointer;
`;

const Wrapper = styled.div`
  display: grid;
  align-items: center;
  gap: ${Spacings.SPACING_12B};
  padding-top: ${Spacings.SPACING_5B};
  padding-bottom: ${Spacings.SPACING_5B};
  grid-template-columns: 1fr 3fr;
`;

const SearchWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
`;

const ImageWrapper = styled.div<IImageWrapper>`
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  background-color: ${({ $open }) =>
    $open ? 'var(--color-divider)' : 'var(--color-base-1)'};
  box-shadow: ${boxShadowGenerator({ color: 'var(--color-shadow)' })};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  &:hover {
    background-color: var(--color-divider);
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin: auto;
    width: 100%;
    grid-template-columns: repeat(18, 1fr);
  }
  @media only screen and (max-width: 728px) {
    grid-template-columns: none;
    width: 100%;
  }
`;

const StyPopper = styled(Popper)`
  margin-top: ${Spacings.SPACING_3B};
`;

const NavigationWrapper = styled.div`
  height: 21px;
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider-secondary);
  border-radius: ${Spacings.SPACING_1B};
  text-transform: uppercase;
  color: var(--color-text-primary);
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.P2};
  line-height: ${Fonts.ULTRA_SMALL};
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: ${Spacings.SPACING_6};
  padding-left: ${Spacings.SPACING_6};
`;

const Header: React.FC<HeaderProps> = ({
  showNavigation,
  onNavigationClick,
  showPDFViewer
}) => {
  const { currentNotesUID } = useContext(EpubContext);
  const popoverAnchor = useRef(null);
  let timerRef = null;
  const {
    query: { goalUID, topicUID, goalSlug }
  } = useRouter();

  const [open, setOpen] = useState(false);
  const [isThemePopupOpen, setIsThemePopupOpen] = useState(false);
  const [popupAnchorEl, setPopupAnchorEl] = useState(null);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef);
    };
  }, []);

  useEffect(() => {
    if (!popupAnchorEl) {
      clearTimeout(timerRef);
      setOpen(false);
    }
  }, [popupAnchorEl]);

  const onNavigationHover = (e) => {
    setPopupAnchorEl(e.target);
    timerRef = setTimeout(() => {
      setOpen(true);
    }, 1000);
  };
  const onNavigationLeave = () => {
    setOpen(false);
    setPopupAnchorEl(null);
  };
  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });

  const onFontClick = (e) => {
    segment.track(
      NOTES_TEXT_SETTINGS_HOVERED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_topic: topicUID,
        notes_uid: currentNotesUID
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    setIsThemePopupOpen(true);
  };

  const handleHidePopup = () => {
    setIsThemePopupOpen(false);
  };

  const onToggle = () => {
    onNavigationClick();
  };

  const onClickLogo = () => {
    pushRoute(`/goal/${goalSlug}/${goalUID}/planner`);
  };

  return (
    <Wrapper>
      <LogoWrapper onClick={onClickLogo}>
        <LogoStyled src={getImage('logo.svg')} alt="Company Logo" />
      </LogoWrapper>
      <InnerWrapper>
        <SearchWrapper>
          <ImageWrapper
            onMouseEnter={onNavigationHover}
            onMouseLeave={onNavigationLeave}
            onClick={onToggle}
          >
            {showNavigation ? (
              <Toggle
                color="var(--color-base-fill)"
                // @ts-ignore
                height={Spacings.SPACING_6B}
                width={Spacings.SPACING_6B}
              />
            ) : (
              <ToggleShowNav
                color="var(--color-base-fill)"
                // @ts-ignore
                height={Spacings.SPACING_6B}
                width={Spacings.SPACING_6B}
              />
            )}
          </ImageWrapper>
          {!showPDFViewer && <SearchBox />}
        </SearchWrapper>

        {!showPDFViewer && (
          <ThemePopover
            triggerRef={popoverAnchor}
            isThemePopupOpen={isThemePopupOpen}
            handleHidePopup={handleHidePopup}
          >
            <ImageWrapper
              ref={popoverAnchor}
              onClick={onFontClick}
              $open={isThemePopupOpen}
            >
              <FontsIcon
                color="var(--color-base-fill)"
                // @ts-ignore
                height={Spacings.SPACING_6B}
                width={Spacings.SPACING_6B}
              />
            </ImageWrapper>
          </ThemePopover>
        )}
      </InnerWrapper>
      <StyPopper
        open={open && popupAnchorEl}
        anchorEl={popupAnchorEl}
        placement="bottom"
      >
        <NavigationWrapper>
          {showNavigation ? 'Hide' : 'Show'} navigation (N)
        </NavigationWrapper>
      </StyPopper>
    </Wrapper>
  );
};

export default Header;
